import React, { useCallback, useEffect, useState } from 'react';
import SupplierPageLayout from '../../components/SupplierPageLayout';
import SupplierEmptyState from '../../components/SupplierEmptyState';
import {
  SupplierCatalogItem,
  SupplierOrganization,
  getCatalog,
  getOrganizations,
  updateCatalogStock,
} from '../../services/supplierApi';

interface StockDraft {
  stock: string;
  available: boolean;
}

const orgLabel = (org: SupplierOrganization) => org.tradeName || org.legalName || `Organisation #${org.id}`;

const toDraft = (item: SupplierCatalogItem): StockDraft => ({
  stock: item.stockQuantity != null ? String(item.stockQuantity) : '',
  available: item.isAvailable !== false,
});

const Inventory: React.FC = () => {
  const [orgs, setOrgs] = useState<SupplierOrganization[]>([]);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [items, setItems] = useState<SupplierCatalogItem[]>([]);
  const [drafts, setDrafts] = useState<Record<number, StockDraft>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    getOrganizations()
      .then((data) => {
        const list: SupplierOrganization[] = data.organizations ?? [];
        setOrgs(list);
        setOrgId(list.length > 0 ? list[0].id : null);
        if (list.length === 0) setLoading(false);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const loadItems = useCallback((id: number) => {
    setLoading(true);
    return getCatalog(id)
      .then((data) => {
        const list: SupplierCatalogItem[] = data.items ?? [];
        setItems(list);
        const next: Record<number, StockDraft> = {};
        list.forEach((item) => {
          if (item.id != null) next[item.id] = toDraft(item);
        });
        setDrafts(next);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (orgId != null) loadItems(orgId);
  }, [orgId, loadItems]);

  const updateDraft = (id: number, patch: Partial<StockDraft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSave = async (item: SupplierCatalogItem) => {
    if (orgId == null || item.id == null) return;
    const draft = drafts[item.id];
    if (!draft) return;
    setError('');
    setNotice('');
    const trimmed = draft.stock.trim();
    const stock = trimmed ? Number(trimmed) : null;
    if (stock != null && (!Number.isFinite(stock) || stock < 0)) {
      setError(`Enter a stock of 0 or more for ${item.itemName}, or leave it blank to stop tracking.`);
      return;
    }
    setSavingId(item.id);
    try {
      const data = await updateCatalogStock(orgId, item.id, stock, draft.available);
      const saved: SupplierCatalogItem = data.item;
      setItems((prev) => prev.map((i) => (i.id === saved.id ? saved : i)));
      if (saved.id != null) {
        const savedId = saved.id;
        setDrafts((prev) => ({ ...prev, [savedId]: toDraft(saved) }));
      }
      setNotice(
        saved.isAvailable === false
          ? `${item.itemName} saved. It is marked out of stock and hidden from restaurants.`
          : `${item.itemName} saved.`,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const isDirty = (item: SupplierCatalogItem) => {
    if (item.id == null || !drafts[item.id]) return false;
    const original = toDraft(item);
    const draft = drafts[item.id];
    return original.stock !== draft.stock.trim() || original.available !== draft.available;
  };

  return (
    <SupplierPageLayout
      wide
      title="Inventory"
      subtitle="Keep stock and availability current so restaurants only see items you can supply."
      icon="fa-cubes"
      breadcrumbs={[
        { label: 'Dashboard', to: '/supplier/dashboard' },
        { label: 'Inventory' },
      ]}
    >
      {error && <div className="sp-alert sp-alert--error">{error}</div>}
      {notice && <div className="sp-alert sp-alert--success">{notice}</div>}

      {!loading && orgs.length === 0 ? (
        <SupplierEmptyState
          icon="fa-cubes"
          title="No supplier organisation yet"
          message="Register your business as a supplier to start tracking stock."
        />
      ) : (
        <>
          {orgs.length > 1 && orgId != null && (
            <div className="sp-actions" style={{ marginTop: 0 }}>
              <select
                className="sp-input"
                style={{ maxWidth: 240 }}
                value={orgId}
                onChange={(e) => setOrgId(Number(e.target.value))}
                aria-label="Organisation"
              >
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {orgLabel(o)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <div className="sp-loading">Loading inventory…</div>
          ) : items.length === 0 ? (
            <SupplierEmptyState
              icon="fa-cubes"
              title="No catalog items"
              message="Add items on the Catalog page first, then track their stock here."
            />
          ) : (
            <>
              <p className="sp-muted" style={{ fontSize: 12, margin: '12px 0 0' }}>
                Leave stock blank if you do not track it. Items with zero stock are marked unavailable and hidden
                from restaurants in the marketplace.
              </p>
              <div className="sp-table-wrap" style={{ marginTop: 12 }}>
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>SKU</th>
                      <th>Pack size</th>
                      <th>Stock on hand</th>
                      <th>Available</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      if (item.id == null) return null;
                      const id = item.id;
                      const draft = drafts[id] ?? toDraft(item);
                      const stockValue = draft.stock.trim() ? Number(draft.stock) : null;
                      const forcedOff = stockValue != null && stockValue <= 0;
                      return (
                        <tr key={id}>
                          <td>{item.itemName}</td>
                          <td>{item.supplierSku || '—'}</td>
                          <td>{item.packSize ?? '—'}</td>
                          <td>
                            <input
                              className="sp-input"
                              type="number"
                              min="0"
                              step="any"
                              style={{ maxWidth: 140 }}
                              value={draft.stock}
                              placeholder="Not tracked"
                              aria-label={`Stock for ${item.itemName}`}
                              onChange={(e) => updateDraft(id, { stock: e.target.value })}
                            />
                          </td>
                          <td>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={draft.available && !forcedOff}
                                disabled={forcedOff}
                                onChange={(e) => updateDraft(id, { available: e.target.checked })}
                              />
                              {forcedOff ? 'Out of stock' : draft.available ? 'Yes' : 'No'}
                            </label>
                          </td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <button
                              type="button"
                              className="sp-btn sp-btn--primary"
                              disabled={savingId != null || !isDirty(item)}
                              onClick={() => handleSave(item)}
                            >
                              {savingId === id ? 'Saving…' : 'Save'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </SupplierPageLayout>
  );
};

export default Inventory;
